import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useMissions } from '../hooks/useMissions';
import MissionCompletionModal from './MissionCompletionModal';
import ImmersiveMissionScreen from './ImmersiveMissionScreen';
import MissionImpactBadge from './MissionImpactBadge';

interface Mission {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  time_frame: 'daily' | 'weekly' | 'monthly';
  verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
  icon: string;
  trending: boolean;
  created_at: string;
  completed?: boolean;
  coming_soon?: boolean;
  launch_date?: string;
  // AR detection parameters
  ar_enabled?: boolean;
  target_objects?: string[];
  target_count?: number;
  // Permanent mission parameters
  is_permanent?: boolean;
  max_completions_per_day?: number;
  cooldown_hours?: number;
  completions_today?: number;
  last_completed?: string;
  can_complete?: boolean;
  next_available?: string;
}

interface MissionCardProps {
  mission: Mission;
}

export default function MissionCard({ mission }: MissionCardProps) {
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [isImmersiveMode, setIsImmersiveMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const { completeMission } = useMissions();

  // Get the icon component dynamically
  const IconComponent = (LucideIcons as any)[mission.icon] || LucideIcons.Target;

  // Calculate countdown for coming soon missions
  React.useEffect(() => {
    if (mission.coming_soon && mission.launch_date) {
      const updateCountdown = () => {
        const now = new Date().getTime();
        const launchTime = new Date(mission.launch_date!).getTime();
        const difference = launchTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          if (days > 0) {
            setTimeLeft(`${days}d ${hours}h ${minutes}m`);
          } else if (hours > 0) {
            setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
          } else {
            setTimeLeft(`${minutes}m ${seconds}s`);
          }
        } else {
          setTimeLeft('Available now!');
        }
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [mission.coming_soon, mission.launch_date]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeFrameColor = (timeFrame: string) => {
    switch (timeFrame) {
      case 'daily': return 'bg-blue-100 text-blue-800';
      case 'weekly': return 'bg-purple-100 text-purple-800';
      case 'monthly': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationMethodLabel = (method: string) => {
    switch (method) {
      case 'photo': return 'Photo Required';
      case 'location': return 'Location Verification';
      case 'quiz': return 'Knowledge Check';
      case 'self-report': return 'Self Report';
      default: return method;
    }
  };

  const getVerificationIcon = (method: string) => {
    switch (method) {
      case 'photo': return LucideIcons.Camera;
      case 'location': return LucideIcons.MapPin;
      case 'quiz': return LucideIcons.HelpCircle;
      case 'self-report': return LucideIcons.CheckSquare;
      default: return LucideIcons.Info;
    }
  };

  const shouldUseImmersiveMode = (mission: Mission) => {
    return mission.ar_enabled === true && mission.verification_method === 'photo';
  };

  const handleStartMission = () => {
    if (mission.coming_soon || !mission.can_complete) {
      return;
    }

    if (shouldUseImmersiveMode(mission)) {
      setIsImmersiveMode(true);
    } else if (mission.verification_method === 'self-report') {
      handleQuickComplete();
    } else {
      setIsCompletionModalOpen(true);
    }
  };

  const handleQuickComplete = async () => {
    setIsLoading(true);
    try {
      await completeMission(mission.id);
    } catch (error) {
      console.error('Error completing mission:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMissionComplete = async (completionData: any) => {
    try {
      await completeMission(mission.id, completionData.proof_url);
      setIsCompletionModalOpen(false);
      setIsImmersiveMode(false);
    } catch (error) {
      console.error('Error completing mission:', error);
      throw error;
    }
  };

  // Completed one-time mission
  if (mission.completed && !mission.is_permanent) {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-500 p-2 rounded-lg">
              <LucideIcons.Check className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{mission.title}</h4>
              <p className="text-sm text-emerald-600">Mission Completed!</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-emerald-600">+{mission.points}</p>
            <p className="text-xs text-emerald-500">EcoCoins earned</p>
          </div>
        </div>
        
        <div className="bg-emerald-100 rounded-lg p-3">
          <p className="text-sm text-emerald-700 text-center">
            Great job! Your impact is making a difference. 🌱
          </p>
        </div>
      </div>
    );
  }

  // Coming Soon Mission Card
  if (mission.coming_soon) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 transition-all duration-300 relative overflow-hidden">
        <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
          COMING SOON
        </div>

        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <IconComponent className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1 flex-wrap">
                <h4 className="font-semibold text-gray-900">{mission.title}</h4>
                {mission.trending && (
                  <LucideIcons.TrendingUp className="h-4 w-4 text-orange-500" />
                )}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{mission.description}</p>
            </div>
          </div>
        </div>

        {mission.launch_date && timeLeft && (
          <div className="mb-4 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <LucideIcons.Calendar className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">
                {timeLeft === 'Available now!' ? 'Available now!' : 'Launches in:'}
              </span>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold font-mono ${timeLeft === 'Available now!' ? 'text-green-600' : 'text-purple-600'}`}>
                {timeLeft}
              </p>
              {timeLeft !== 'Available now!' && (
                <p className="text-xs text-purple-600 mt-1">
                  {new Date(mission.launch_date).toLocaleDateString()} at {new Date(mission.launch_date).toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission.difficulty)}`}>
            {mission.difficulty}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTimeFrameColor(mission.time_frame)}`}>
            {mission.time_frame}
          </span>
          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
            {mission.category}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
            <span className="text-lg font-bold text-gray-900">{mission.points} EcoCoins</span>
          </div>
          
          <div className="bg-gray-200 text-gray-500 px-4 py-2 rounded-lg font-medium cursor-not-allowed flex items-center space-x-2">
            <LucideIcons.Lock className="h-4 w-4" />
            <span>Coming Soon</span>
          </div>
        </div>

        <div className="absolute top-2 left-2 w-2 h-2 bg-purple-400 rounded-full animate-ping"></div>
        <div className="absolute bottom-2 right-2 w-1 h-1 bg-pink-400 rounded-full animate-pulse"></div>
      </div>
    );
  }

  const VerificationIcon = getVerificationIcon(mission.verification_method);

  return (
    <>
      <div className={`bg-white border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${
        mission.is_permanent ? 'border-l-4 border-l-blue-500' : 'border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${mission.is_permanent ? 'bg-blue-100' : 'bg-emerald-100'}`}>
              <IconComponent className={`h-5 w-5 ${mission.is_permanent ? 'text-blue-600' : 'text-emerald-600'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1 flex-wrap">
                <h4 className="font-semibold text-gray-900">{mission.title}</h4>
                {mission.trending && (
                  <LucideIcons.TrendingUp className="h-4 w-4 text-orange-500" />
                )}
                {shouldUseImmersiveMode(mission) && (
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                    AR Mode
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{mission.description}</p>
            </div>
          </div>
        </div>

        {/* Permanent Mission Progress */}
        {mission.is_permanent && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <LucideIcons.RotateCcw className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Permanent Mission</span>
              </div>
              <span className="text-xs text-blue-600">
                {mission.completions_today || 0}/{mission.max_completions_per_day || 1} today
              </span>
            </div>
            
            {!mission.can_complete && mission.next_available && (
              <div className="flex items-center space-x-2">
                <LucideIcons.Clock className="h-3 w-3 text-blue-600" />
                <span className="text-xs text-blue-600">
                  Next available: {mission.next_available}
                </span>
              </div>
            )}
            
            {mission.can_complete && (
              <div className="flex items-center space-x-2">
                <LucideIcons.CheckCircle className="h-3 w-3 text-green-600" />
                <span className="text-xs text-green-600">Ready to complete again!</span>
              </div>
            )}
          </div>
        )}

        {/* AR Detection Info */}
        {shouldUseImmersiveMode(mission) && mission.target_objects && (
          <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <LucideIcons.Eye className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">AR Detection Target</span>
            </div>
            <div className="text-xs text-purple-600">
              <span className="font-medium">Objects to find:</span> {mission.target_objects.join(', ')}
              {mission.target_count && mission.target_count > 1 && (
                <span className="ml-2">({mission.target_count} items needed)</span>
              )}
            </div>
          </div>
        )}

        {/* Environmental Impact Badge */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <MissionImpactBadge missionId={mission.id} />
            <VerificationIcon className="h-4 w-4" />
            <span>{getVerificationMethodLabel(mission.verification_method)}</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission.difficulty)}`}>
            {mission.difficulty}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTimeFrameColor(mission.time_frame)}`}>
            {mission.time_frame}
          </span>
          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
            {mission.category}
          </span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            <span className="text-lg font-bold text-gray-900">+{mission.points} EcoCoins</span>
          </div>
          
          <button
            onClick={handleStartMission}
            disabled={isLoading || !mission.can_complete}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 ${
              !mission.can_complete
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : shouldUseImmersiveMode(mission)
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl'
                : mission.is_permanent
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <>
                <LucideIcons.Loader2 className="h-4 w-4 animate-spin" />
                <span>Completing...</span>
              </>
            ) : !mission.can_complete ? (
              <>
                <LucideIcons.Clock className="h-4 w-4" />
                <span>
                  {mission.next_available ? `Wait ${mission.next_available}` : 'Completed'}
                </span>
              </>
            ) : shouldUseImmersiveMode(mission) ? (
              <>
                <LucideIcons.Zap className="h-4 w-4" />
                <span>Start AR Mission</span>
              </>
            ) : mission.is_permanent ? (
              <>
                <LucideIcons.RotateCcw className="h-4 w-4" />
                <span>Complete Again</span>
              </>
            ) : (
              <>
                <LucideIcons.Play className="h-4 w-4" />
                <span>Start Mission</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Traditional Mission Completion Modal */}
      <MissionCompletionModal
        mission={mission}
        isOpen={isCompletionModalOpen}
        onClose={() => setIsCompletionModalOpen(false)}
        onComplete={handleMissionComplete}
      />

      {/* Immersive AR Mission Screen */}
      <ImmersiveMissionScreen
        mission={mission}
        isOpen={isImmersiveMode}
        onClose={() => setIsImmersiveMode(false)}
        onComplete={handleMissionComplete}
      />
    </>
  );
}